import { auth } from './firebase';
import { getIdToken } from 'firebase/auth';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8080';

/**
 * Fase 3 — Evento customizado disparado quando o backend retorna 402 com
 * code `INSUFFICIENT_CREDITS`. Um listener global em `AppContent` recebe
 * esse evento e abre o `NoCreditsModal`.
 */
export const INSUFFICIENT_CREDITS_EVENT = 'insufficient-credits';

/**
 * Disparado quando uma operação concluída pode ter alterado o saldo de
 * créditos. Com o débito movido para o fim da correção, é emitido pelo
 * `useGrading` ao receber o evento SSE `done`. `useCredits` escuta e refaz o
 * fetch de `/me/credits` para o saldo exibido refletir o consumo.
 */
export const CREDITS_CHANGED_EVENT = 'credits-changed';

/**
 * Fase 9 — Evento customizado disparado quando o backend recusa um recurso da
 * assinatura com 403 `SUBSCRIPTION_REQUIRED`. Um listener global em
 * `AppContent` recebe e abre o `SubscriptionPaywall`. Mesmo padrão do 402.
 *
 * Também é disparado pelo `useSubscriptionGate` quando a UI decide abrir o
 * paywall antes de chamar o backend (o clique num recurso pago); nesse caso o
 * detail carrega o `origin` da superfície.
 */
export const SUBSCRIPTION_REQUIRED_EVENT = 'subscription-required';

export interface InsufficientCreditsEventDetail {
  actionType?: string;
  endpoint?: string;
}

export interface SubscriptionRequiredEventDetail {
  /** `FREE` (assine) ou `READ_ONLY` (regularize) — vem do corpo do 403. */
  accessMode?: string;
  /** Superfície que originou o paywall; ausente quando veio de um 403. */
  origin?: string;
  endpoint?: string;
}

/**
 * Helper puro (testável) — decide se uma resposta deve disparar o evento
 * `insufficient-credits`. Condição: status === 402 e `body.code` é
 * `INSUFFICIENT_CREDITS`.
 */
export function shouldDispatchInsufficientCredits(status: number, body: unknown): boolean {
  if (status !== 402) return false;
  if (!body || typeof body !== 'object') return false;
  const code = (body as { code?: unknown }).code;
  return code === 'INSUFFICIENT_CREDITS';
}

async function maybeDispatchInsufficientCredits(
  response: Response,
  endpoint: string
): Promise<void> {
  if (response.status !== 402) return;
  try {
    const clone = response.clone();
    const body = (await clone.json()) as Record<string, unknown>;
    if (!shouldDispatchInsufficientCredits(response.status, body)) return;
    const detail: InsufficientCreditsEventDetail = {
      actionType: typeof body.actionType === 'string' ? body.actionType : undefined,
      endpoint,
    };
    window.dispatchEvent(new CustomEvent(INSUFFICIENT_CREDITS_EVENT, { detail }));
  } catch {
    // Resposta 402 sem body JSON — não dispara evento.
  }
}

/**
 * Helper puro (testável) — decide se uma resposta deve disparar o evento
 * `subscription-required`.
 *
 * Condições: status 403, `body.code === 'SUBSCRIPTION_REQUIRED'` e o método
 * NÃO é GET. O recorte por método é deliberado: `/progress` e `/competencies`
 * são GETs de leitura que a página faz sozinha ao montar — abrir um modal em
 * cima deles transformaria "entrar no dashboard" em "levar um paywall na
 * cara". Essas superfícies renderizam estado bloqueado (`LockedFeatureCard`).
 * Já POSTs (`/grade/start`, `/action/stream`, `/insights`) são sempre uma ação
 * que a pessoa acabou de pedir — aí o modal é a resposta certa.
 */
export function shouldDispatchSubscriptionRequired(
  status: number,
  body: unknown,
  method: string | undefined
): boolean {
  if (status !== 403) return false;
  if ((method ?? 'GET').toUpperCase() === 'GET') return false;
  if (!body || typeof body !== 'object') return false;
  return (body as { code?: unknown }).code === 'SUBSCRIPTION_REQUIRED';
}

/**
 * `true` quando a resposta é o 403 `SUBSCRIPTION_REQUIRED` do gate de
 * assinatura — o caller distingue "recusado por não assinar" (o paywall já foi
 * aberto por `apiClient`; cabe seguir para a alternativa gratuita) de falha
 * técnica. Lê um clone: a resposta original continua consumível.
 */
export async function isSubscriptionRequired(response: Response): Promise<boolean> {
  if (response.status !== 403) return false;
  try {
    const body = (await response.clone().json()) as { code?: unknown };
    return body?.code === 'SUBSCRIPTION_REQUIRED';
  } catch {
    return false;
  }
}

async function maybeDispatchSubscriptionRequired(
  response: Response,
  endpoint: string,
  method: string | undefined
): Promise<void> {
  if (response.status !== 403) return;
  try {
    const clone = response.clone();
    const body = (await clone.json()) as Record<string, unknown>;
    if (!shouldDispatchSubscriptionRequired(response.status, body, method)) return;
    const detail: SubscriptionRequiredEventDetail = {
      accessMode: typeof body.accessMode === 'string' ? body.accessMode : undefined,
      endpoint,
    };
    window.dispatchEvent(new CustomEvent(SUBSCRIPTION_REQUIRED_EVENT, { detail }));
  } catch {
    // 403 sem body JSON (ex.: auth) — não é o paywall.
  }
}

/**
 * Cliente API que adiciona automaticamente o token de autenticação.
 * Use esta função em componentes client-side.
 *
 * Efeito colateral (Fase 3): quando a resposta é 402 com
 * `code: 'INSUFFICIENT_CREDITS'`, dispara um `CustomEvent` global para o
 * `NoCreditsModal` antes de retornar a resposta original ao caller.
 */
export async function apiClient(endpoint: string, options: RequestInit = {}): Promise<Response> {
  const token = auth?.currentUser ? await getIdToken(auth.currentUser) : null;

  const headers: HeadersInit = {
    ...options.headers,
  };

  // Só definir Content-Type JSON quando houver body e não for FormData
  const isFormData = options.body instanceof FormData;
  const hasBody = options.body !== undefined && options.body !== null;
  if (!isFormData && hasBody) {
    headers['Content-Type'] =
      (headers as Record<string, string>)['Content-Type'] || 'application/json';
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  // Impersonation: admin visualizando como outro usuário
  const impersonateUid = sessionStorage.getItem('impersonate-uid');
  if (impersonateUid) {
    headers['X-Impersonate-User'] = impersonateUid;
  }

  const url = endpoint.startsWith('http') ? endpoint : `${BACKEND_URL}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers,
  });

  await maybeDispatchInsufficientCredits(response, endpoint);
  await maybeDispatchSubscriptionRequired(response, endpoint, options.method);

  return response;
}

/**
 * Hook para usar API client em componentes React
 * Retorna função que aceita endpoint e options
 */
export function useApiClient() {
  return async (endpoint: string, options: RequestInit = {}): Promise<Response> => {
    return apiClient(endpoint, options);
  };
}
